/*
 * ===============================================================================================================
 *                                Copyright 2025, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  LuiMaterialTable,
  LuiToolbar,
  LuiCard,
  LuiEmptyState,
  LuiMaterialTableHeadCellStyles
} from '@jda/lui-common-component-library-mui5';
import { AlertCircle, SignPlus } from '@jda/lui-common-icon-library-mui5';
import { Box, Button, Typography, Tooltip, TableCellProps } from '@mui/material';
import CustomTablePagination from '../../shared/CustomTablePagination';
import { AlertDialog } from '../../shared/Dialog/AlertDialog';
import DetailsModal from '../../shared/Dialog/DetailsModal';
import { getMachineSubscriptions, removeMachineSubscriptions } from '../../services/client';
import { displaySnackbar } from '../../store/snackBar/snackBarActionCreator';
import {
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton
} from 'material-react-table';
import {
  ADD_SUBSCRIPTION,
  UNSUBSCRIBE,
  EMPTY_HEADER,
  EMPTY_TEXT,
  NAME,
  API_VERSION,
  MAX_SUBSCRIPTION_SELECTION,
  ROWS_PER_PAGE_LABEL,
  UNSUBSCRIBE_APIS,
  UNSUBSCRIBE_CONFIRMATION,
  APIS,
  CANCEL,
  APIS_UNSUBSCRIBED_SUCCESSFULLY,
  UNSUCCESSFUL_UNSUBSCRIBE_TITLE,
  UNSUCCESSFUL_UNSUBSCRIBE_MESSAGE,
  SUBSCRIBED_APIS,
  SELECTED_OF,
  // URI,
  // MODULE_KEY,
  RESTRICTION_MESSAGE_NEW
} from './constants';
import { useStyles } from './styles';

const INITIAL_PAGE_NUMBER = 1;
const INITIAL_PAGE_SIZE = 10;

const columns = [
  {
    header: NAME,
    accessorKey: 'apiDisplayName',
    id: 'apiDisplayName',
    muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          width: '35%'
        })
      } as TableCellProps)
  },
  {
    header: API_VERSION,
    accessorKey: 'apiVersion',
    id: 'apiVersion',
    muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          width: '15%'
        })
      } as TableCellProps)
  },
  // {
  //   header: URI,
  //   accessorKey: 'apiUri',
  //   id: 'apiUri',
  //   muiTableHeadCellProps: ({ column, table }) =>
  //     ({
  //       sx: theme => ({
  //         ...LuiMaterialTableHeadCellStyles({
  //           column,
  //           theme,
  //           table,
  //           styling: 'standard',
  //           virtualizationEnabled: false
  //         }),
  //         width: '35%'
  //       })
  //     } as TableCellProps)
  // },  
  // {
  //   header: MODULE_KEY,
  //   accessorKey: 'moduleKey',
  //   id: 'moduleKey',
  //   muiTableHeadCellProps: ({ column, table }) =>
  //     ({
  //       sx: theme => ({
  //         ...LuiMaterialTableHeadCellStyles({
  //           column,
  //           theme,
  //           table,
  //           styling: 'standard',
  //           virtualizationEnabled: false
  //         }),
  //         width: '15%'
  //       })
  //     } as TableCellProps)
  // }
];

interface ApiSubscriptionsProps {
  machineId: string;
}

const ApiSubscriptions = ({ machineId }: ApiSubscriptionsProps) => {
  const [pageNumber, setPageNumber] = useState(INITIAL_PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [response, setResponse] = useState<any>();
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<any[]>([]);
  const [unsubscribingApis, setUnsubscribingApis] = useState<any[]>([]);
  const [isRequesting, setIsRequesting] = useState(false);
  const [erroredSubscriptions, setErroredSubscriptions] = useState<any[]>([]);

  const mappedData = useMemo(() => response?.data?.map((api: any) => ({ ...api, id: api.subscriptionId })) ?? [], [
    response
  ]);

  const history = useHistory();

  const dispatch = useDispatch();

  const { classes } = useStyles();

  const fetchMachineSubscriptions = useCallback(
    async (itemsPerPage: number, page: number) => {
      setPageSize(itemsPerPage);
      setPageNumber(page);
      try {
        const response = await getMachineSubscriptions(machineId, { 'page-number': page, 'page-size': itemsPerPage });
        setResponse(response);
      } catch (error) {
        console.log(error);
      }
    },
    [machineId]
  );

  useEffect(() => {
    fetchMachineSubscriptions(INITIAL_PAGE_SIZE, INITIAL_PAGE_NUMBER);
  }, [fetchMachineSubscriptions]);

  const handleAddSubscriptionClick = async () => {
    if (history.location.search) {
      history.push(`/machineDetail/addSubscriptions/${machineId}${history.location.search}`);
    } else {
      history.push(`/machineDetail/addSubscriptions/${machineId}`);
    }
  };

  const handleUnsubscribeSelected = () => {
    setUnsubscribingApis(selectedSubscriptions);
  };

  const handleUnsubscribeSave = async () => {
    setIsRequesting(true);

    const promiseResults = await Promise.all(
      unsubscribingApis.map(async api => {
        try {
          await removeMachineSubscriptions(machineId, [{ name: api.apiName, version: api.apiVersion }]);
        } catch {
          return api;
        }
      })
    );

    setIsRequesting(false);

    const erroredSubscriptions = promiseResults.filter(result => result !== undefined);

    if (erroredSubscriptions.length > 0) {
      setUnsubscribingApis([]);
      setErroredSubscriptions(erroredSubscriptions);
      return;
    }

    setSelectedSubscriptions([]);
    setUnsubscribingApis([]);
    fetchMachineSubscriptions(pageSize, 1);
    dispatch(displaySnackbar(APIS_UNSUBSCRIBED_SUCCESSFULLY, 'success'));
  };

  const handleUnsubscribeCancel = () => {
    setUnsubscribingApis([]);
  };

  const handleErroredApisModalClose = () => {
    setErroredSubscriptions([]);
  };
  const extendedToolbar = ({ table }) => {
    const rightComponents = {
      component: (
        <div className={classes.toolbarButtonPanel}>
          <Tooltip arrow title={ADD_SUBSCRIPTION} placement="bottom">
            <Button variant="outlined" color="primary" onClick={handleAddSubscriptionClick} startIcon={<SignPlus />}>
              {ADD_SUBSCRIPTION}
            </Button>
          </Tooltip>
          <Tooltip arrow title={UNSUBSCRIBE} placement="bottom">
            <Button
              variant="outlined"
              color="primary"
              disabled={selectedSubscriptions.length === 0 || selectedSubscriptions.length > MAX_SUBSCRIPTION_SELECTION}
              onClick={handleUnsubscribeSelected}
            >
              {UNSUBSCRIBE}
            </Button>
          </Tooltip>
          <ShowHideColumnsButton table={table} />
          <ToggleDensePaddingButton table={table} />
          <ToggleFullScreenButton table={table} />
        </div>
      )
    };
    return <LuiToolbar className={classes.toolbarPanel} rightZone={rightComponents} />;
  };
  const emptyTableFallback = () => {
    return (
      <Box my={5}>
        <LuiEmptyState imageItem={null} heading={EMPTY_HEADER} subHeading={EMPTY_TEXT} />
      </Box>
    );
  };
  return (
    <>
      <LuiCard
        headerBordered={true}
        hasCardContentPadding={false}
        elevation={0}
        headerComponent={
          <div className={classes.cardHeader}>
            <Typography variant="h2" color="textPrimary">
              {SUBSCRIBED_APIS}
            </Typography>
            <Typography variant="body1" color="textPrimary">
              {selectedSubscriptions.length > 0
                ? `(${selectedSubscriptions.length} ${SELECTED_OF} ${response?.metadata?.totalCount})`
                : ''}
            </Typography>
          </div>
        }
      >
        <CustomTablePagination
          data={mappedData}
          loading={!response}
          withoutPagination={!response}
          totalCount={response?.metadata?.totalCount ?? 0}
          columns={columns}
          selectedRowsData={selectedSubscriptions}
          onSelectedRowsDataChange={setSelectedSubscriptions}
          rowsPerPageOptions={[1, 5, 10]}
          labelRowsPerPage={ROWS_PER_PAGE_LABEL}
          currentPageNumber={pageNumber}
          currentItemsPerPage={pageSize}
          onChangePageOptions={fetchMachineSubscriptions}
          renderTopToolbar={extendedToolbar}
          renderEmptyRowsFallback={emptyTableFallback}
          additionalContent={
            response ? (
              <Box className={classes.alertMsg}>
                <AlertCircle className={classes.warningIcon} />
                <Typography variant="body1">{RESTRICTION_MESSAGE_NEW}</Typography>
              </Box>
            ) : null
          }
        />
      </LuiCard>
      {unsubscribingApis.length > 0 && (
        <AlertDialog
          dialogContentText={UNSUBSCRIBE_APIS}
          dialogTitle={`${UNSUBSCRIBE_CONFIRMATION} `}
          recordName={
            unsubscribingApis.length === 1 ? unsubscribingApis[0].apiName : `${unsubscribingApis.length} ${APIS}`
          }
          hideCanNotBeUndoneMessage={true}
          primaryButtonText={UNSUBSCRIBE}
          secondButtonText={CANCEL}
          handleOnOk={handleUnsubscribeSave}
          handleOnCancel={handleUnsubscribeCancel}
          showDialogLoader={isRequesting}
        />
      )}
      {erroredSubscriptions.length > 0 && (
        <DetailsModal
          openModal={true}
          handleClose={handleErroredApisModalClose}
          dialogTitle={UNSUCCESSFUL_UNSUBSCRIBE_TITLE}
        >
          <Typography>{UNSUCCESSFUL_UNSUBSCRIBE_MESSAGE}</Typography>
          <LuiMaterialTable data={erroredSubscriptions} keyAccessor="id" columns={columns} enableAlternateRowStyle />
        </DetailsModal>
      )}
    </>
  );
};

export default ApiSubscriptions;
